import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import BusinessCard from '../components/BusinessCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import { ArrowLeft, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const query = searchParams.get('q') || '';
  
  const [searchQuery, setSearchQuery] = useState(query);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (query) {
      fetchResults();
    } else {
      setLoading(false);
    }
  }, [query]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/businesses?search=${encodeURIComponent(query)}`);
      setBusinesses(response.data);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const pageTitle = query 
    ? `Search Results for "${query}" | United Hands Karur Shop`
    : 'Search Businesses | United Hands Karur Shop';
  
  const pageDescription = query
    ? `Find businesses matching "${query}" in Karur. Search our directory of local shops, services, and businesses.`
    : 'Search for local businesses, shops, and services in Karur. Find phone numbers, addresses, reviews and more.';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="search-page">
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords={`${query ? query + ', ' : ''}search businesses Karur, find shops, business directory search, Karur local search`}
      />
      
      {/* Header */}
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-purple-800 py-12 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-10 right-10 w-64 h-64 bg-purple-300 rounded-full filter blur-3xl animate-floatSlow"></div>
        </div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/20 mb-6 btn-scale"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('home')}
          </Button>
          
          {/* Search Form - Improved Design */}
          <form onSubmit={handleSearch} className="max-w-3xl mx-auto">
            <div className="relative flex items-center shadow-2xl glow-on-hover">
              <div className="relative flex-1">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-6 w-6 text-slate-400 z-10" />
                <Input
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-16 pl-14 pr-4 rounded-l-full border-0 text-lg placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-white/50 bg-white/95 backdrop-blur-md shadow-xl"
                  data-testid="search-input"
                />
              </div>
              <Button
                type="submit"
                className="h-16 px-10 rounded-r-full bg-gradient-to-r from-[#E85A24] to-orange-600 hover:from-[#d14918] hover:to-orange-700 text-white font-bold text-lg shadow-xl ripple relative overflow-hidden"
                data-testid="search-btn"
              >
                <span className="relative z-10">{t('search')}</span>
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Results */}
      <div className="container mx-auto px-4 md:px-6 py-12">
        {query && (
          <h2 className="text-xl font-semibold text-slate-900 mb-6">
            {language === 'ta' 
              ? `"${query}" க்கான முடிவுகள் (${businesses.length})`
              : `Results for "${query}" (${businesses.length})`
            }
          </h2>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse bg-white rounded-xl h-72" />
            ))}
          </div>
        ) : businesses.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-500 text-lg">{t('noResults')}</p>
            <Button
              onClick={() => navigate('/')}
              className="mt-4"
              variant="outline"
            >
              {t('home')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {businesses.map((business) => (
              <BusinessCard key={business.id} business={business} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
